<template>
	<div
		id="search-container"
		class="w-full h-full"
	>
		<GridLayout :sidebar-state="false">
			<template #right-header>
				<div class="w-full h-full flex items-center gap-3">
					<button
						class="btn btn-rounded"
						@click="Previous"
					>
						<i class="fas fa-chevron-left"></i>
					</button>
					<Steps
						v-if="isReceptionFlow"
						:steps-map="stepsMap"
						:current-step="currentStep"
						@select-step="
							currentStep = $event
							autoNav = false
						"
					/>
					<Title
						v-else
						value="PESQUISA DE AGENDAMENTOS"
					/>
				</div>
			</template>
			<template #right-content>
				<Patient
					v-if="step === 'patient' && isReceptionFlow"
					:patient-id="reception_flow_data.patient_id || null"
					:auto-nav="autoNav"
					:patient="selected.patient"
					class="w-full flex-1 flex flex-col"
					@next="WalkStep"
					@change:autoNav="autoNav = $event"
				/>

				<template v-else>
					<ToolBar>
						<div class="w-full h-full flex items-center">
							<div class="w-4/12">
								<SearchGroup
									:search="search"
									@change="search = $event"
									@search="GetAttendances()"
								/>
							</div>
							<div class="flex-1 h-full py-2 flex justify-end gap-3">
								<div
									id="dropdown-date"
									class="dropdown mb-2 mb-md-0"
								>
									<button
										class="btn btn-outline btn-fixed-size btn-dropdown"
										data-toggle="dropdown"
									>
										<span> DATA</span>
										<i class="fas fa-chevron-down font-18"></i>
									</button>
									<div class="dropdown-menu px-3 py-0">
										<form class="flex flex-col gap-3 py-3">
											<div class="form-group">
												<label class="font-14 mb-1">do dia</label>
												<input
													v-model="filters.date_init"
													type="date"
													class="form-control p-3"
												/>
											</div>
											<div class="form-group">
												<label class="font-14 mb-1">até o dia</label>
												<input
													v-model="filters.date_end"
													type="date"
													class="form-control p-3"
												/>
											</div>
										</form>
									</div>
								</div>
								<div
									id="dropdown-status"
									class="dropdown mb-2 mb-md-0"
								>
									<button
										class="btn btn-outline btn-fixed-size btn-dropdown"
										data-toggle="dropdown"
									>
										<span>STATUS</span>
										<i class="fas fa-chevron-down font-18"></i>
									</button>
									<div class="dropdown-menu px-3 py-0">
										<form class="flex flex-col gap-3 py-3">
											<div
												v-for="(status, index) in scheduling_status"
												:key="index"
												class="form-check"
											>
												<input
													:id="status.name"
													v-model="filters.status"
													type="checkbox"
													:value="status.value"
												/>
												<label :for="status.name">
													{{ status.name }}
												</label>
											</div>
										</form>
									</div>
								</div>
								<button
									class="btn btn-fixed-size"
									@click="GetAttendances()"
								>
									BUSCAR
								</button>
							</div>
						</div>
					</ToolBar>
					<MainContent>
						<NewDatatable
							v-model="attendances"
							:headers="headers"
							:lazy-loading="true"
							@load-table="UpdateTable"
							@dblclick="SelectAttendance"
						>
							<template #row="{ item }">
								<div class="datatable-cell date">
									<b>
										{{ item.date }}
									</b>
								</div>
								<div class="datatable-cell time">
									<b>
										{{ item.time }}
									</b>
								</div>
								<div class="datatable-cell name">
									<b>{{ item.header.patient.name }}</b>
								</div>
								<div class="datatable-cell doc_id">
									{{ `${item.header.patient.doc_id.tipo}: ${item.header.patient.doc_id.numero}` }}
								</div>
								<div class="datatable-cell status">
									<b>{{ item.status_descricao.toUpperCase() }}</b>
								</div>
							</template>
						</NewDatatable>
					</MainContent>
				</template>

				<AttendanceModal
					v-if="showAttendanceModal"
					:origin="'search'"
					:attendance_id="selected_attendance_id"
					@close="showAttendanceModal = false"
				/>
			</template>
			<template #right-info-bar>
				<InfoBar class="flex justify-end">
					<div class="flex items-center gap-2">
						<span>EXIBIDOS: </span>
						<b>{{ attendances.length }}</b>
						<span>DE</span>
						<span>
							<b>{{ total_attendances }}</b>
						</span>
					</div>
				</InfoBar>
			</template>
			<template #right-footer>
				<div
					v-if="isReceptionFlow"
					class="w-full h-full flex items-center"
				>
					<div class="h-full flex gap-3">
						<button
							class="btn btn-danger btn-fixed-size"
							:disabled="reception_flow_data?.is_finished"
							@click="CancelFlow()"
						>
							SUSPENDER
						</button>
					</div>
					<div class="flex-1 h-full flex justify-end">
						<button
							class="btn btn-success btn-fixed-size"
							:disabled="!reception_flow_data?.is_finishable"
							@click="FinishFlow()"
						>
							CONCLUIR
						</button>
					</div>
				</div>
			</template>
		</GridLayout>
	</div>
</template>

<script>
	const _ = require('lodash')
	const moment = require('moment')

	import Swal from 'sweetalert2'
	import { mapGetters, mapActions } from 'vuex'
	import receptionFlowMixin from '@/mixins/receptionFlow'

	import AttendanceModal from '../shared/AttendanceModal'
	import Patient from '@/components/shared/scheduling/Patient'
	import SchedulingCard from './SchedulingCard'

	export default {
		name: 'Search',
		mixins: [receptionFlowMixin],
		components: {
			AttendanceModal,
			Patient,
			SchedulingCard,
		},
		data() {
			return {
				starting: true,
				filters: {
					date_init: '',
					date_end: '',
					date_edit: false,
					status: [],
					status_edit: false,
				},
				limit: 200,
				offset: 0,
				search: '',
				attendances: [],
				headers: [
					{ text: 'DATA', value: 'data', width: '7rem' },
					{ text: 'HORA', value: 'hora', width: '5rem' },
					{ text: 'PACIENTE', value: 'patient.nome_paciente' },
					{
						text: 'ID',
						value: 'patient.doc_id.numero',
					},
					{
						text: 'STATUS',
						value: 'status_descricao',
						width: '12rem',
					},
				],
				timeline_data: {
					patient: {},
					logs: [],
					journey_id: null,
					scheduling_id: null,
				},
				sortedBy: {
					column: 'data',
					direction: 'DESC',
					icon: 'fa-chevron-down',
				},
				total_attendances: 0,
				try_search: false,
				selected_attendance_id: null,
				showAttendanceModal: false,
				selected: {
					patient: null,
				},
				currentStep: 0,
				autoNav: true,
				stepsMap: [
					{
						key: 'pre',
					},
					{
						key: 'patient',
						number: 1,
						label: 'PACIENTE',
						isValid: false,
					},
					{
						key: 'search',
						number: 2,
						label: 'AGENDAMENTOS',
						isValid: true,
					},
				],
			}
		},
		computed: {
			...mapGetters('auth', ['requester_selected_id']),
			...mapGetters('system', ['requests']),
			scheduling_status() {
				return [
					{ name: 'AGENDADO', value: 1 },
					{ name: 'COMPARECIMENTO', value: 21 },
					{ name: 'CONFIRMAÇÃO', value: 22 },
					{ name: 'REAGENDADO', value: 19 },
				]
			},
			first_day_of_month() {
				return moment().startOf('month').format('YYYY-MM-DD')
			},
			last_day_of_month() {
				return moment().endOf('month').format('YYYY-MM-DD')
			},
			step() {
				return this.stepsMap[this.currentStep].key
			},
		},
		watch: {
			'filters.date_init': {
				handler(val) {
					const vm = this

					let message = ''
					let valid = true

					let date_init = val.split('-')
					let date_end = vm.filters.date_end.split('-')

					if (
						new Date(date_end[0], date_end[1], date_end[2]) <
						new Date(date_init[0], date_init[1], date_init[2])
					) {
						Swal.fire({
							title: 'A DATA DE INÍCIO NÃO PODE SER SUPERIOR A DATA FINAL.',
							icon: 'error',
							toast: true,
							position: 'top',
							timer: 4000,
							timerProgressBar: true,
							customClass: {
								container: 'themed',
							},
						})

						vm.filters.date_end = val
					}
				},
			},
			'filters.date_end': {
				handler(val) {
					const vm = this

					let date_end = val.split('-')
					let date_init = vm.filters.date_init.split('-')

					if (
						new Date(date_end[0], date_end[1], date_end[2]) <
						new Date(date_init[0], date_init[1], date_init[2])
					) {
						Swal.fire({
							title: 'A DATA FINAL NÃO PODE SER INFERIOR A DATA DE INÍCIO.',
							icon: 'error',
							toast: true,
							position: 'top',
							timer: 4000,
							timerProgressBar: true,
							customClass: {
								container: 'themed',
							},
						})

						vm.filters.date_end = vm.filters.date_init
					}
				},
			},
			search: {
				handler(val) {
					this.try_search = false
				},
			},
			step: {
				handler(val) {
					if (val === 'search') this.GetAttendances()
				},
				immediate: true,
			},
		},
		methods: {
			...mapActions('system', ['StartLoading', 'FinishLoading']),
			Previous() {
				switch (this.step) {
					case 'patient':
						this.$router.push({ name: 'Home' })
						break
					case 'search':
						if (this.isReceptionFlow) {
							this.currentStep--
						} else {
							this.$router.push({ name: 'Home' })
						}

						break
				}

				this.autoNav = false
			},
			WalkStep(param) {
				switch (this.stepsMap[this.currentStep].key) {
					case 'pre':
						if (this.isReceptionFlow) {
							this.currentStep = 1
						} else {
							this.currentStep = 2
						}

						break
					case 'patient':
						this.selected.patient = param.patient

						this.stepsMap[this.currentStep].isValid = true
						if (param.autoWalkStep) this.currentStep++
						break
				}

				this.autoNav = true
			},
			SelectAttendance(attendance) {
				this.selected_attendance_id = attendance.id

				this.showAttendanceModal = true
			},
			async GetAttendances(clear = true) {
				this.offset = clear ? 0 : this.offset
				this.attendances = clear ? [] : this.attendances

				this.StartLoading()

				return window.api
					.call('post', '/get-attendances', {
						account_id: this.requester_selected_id,
						date_init: this.filters.date_init,
						date_end: this.filters.date_end,
						status: this.filters.status,
						search: this.search,
						limit: this.limit,
						offset: this.offset,
						light_data: true,
						only_schedulings: true,
						latest_first: true,
						patient_id: this.selected.patient?.id || null,
					})
					.then(({ data }) => {
						if (data.response) {
							this.attendances = _.orderBy(
								[...this.attendances, ...data.attendances],
								'timestamp',
								'desc'
							)

							this.total_attendances = data.total_attendances
						}
					})
					.finally(() => {
						this.FinishLoading()
					})
			},
			async DelayGetAttendances() {
				setTimeout(async () => {
					if (!this.try_search) {
						await this.GetAttendances()

						this.try_search = true
					}
				}, 1000)
			},
			async UpdateTable() {
				this.offset += this.limit

				if (this.attendances.length >= this.total_attendances) {
					Swal.fire({
						title: 'NÃO HÁ MAIS AGENDAMENTOS',
						toast: true,
						position: 'top',
						icon: 'info',
						timer: 4000,
						timerProgressBar: true,
						customClass: {
							container: 'themed',
						},
					})

					return
				}

				await this.GetAttendances(false)
			},
		},
		async beforeMount() {
			await this.HandleReceptionFlow()
			this.reception_flow_data.is_finishable = true

			this.WalkStep()
		},
	}
</script>

<style lang="scss" scoped>
	.overflow-search-table {
		overflow: hidden auto;
		max-height: calc(100vh - 100px);
	}

	:deep(.grid-layout .btn.btn-fixed-size.btn-dropdown) {
		justify-content: space-between;
	}

	#search-container {
		.dropdown-menu {
			background-color: var(--elementsBackgroundColor);
			color: var(--color-font-light);
			border: 1px solid var(--elementsBorderColor);
			padding: 0.85rem 0;
			border-radius: 0.5rem;
			min-width: 100%;
			box-shadow: 0px 5px 32px #00000024;
		}

		.dropdown-item {
			width: auto;
			margin: 0 0.85rem;
			padding: 0.5rem 1rem;
			border-radius: 0.5rem;
			transition: all 0.2s ease;
		}

		.dropdown-item:hover {
			background-color: var(--primary);
		}
	}
</style>
